import { useFormContext } from "@4uhub/lib4uhub";
import { memo, useEffect } from "react";
import { Controller, useWatch } from "react-hook-form";
import { IWhatsMetaCategory, TBodyField } from "./models";
import { Grid, InputAdornment, Typography } from "@mui/material";
import { WhatsappSection } from "../WhatsappSection/WhatsappSection";
import { BodyInput } from "./BodyInput/BodyInput";
import { useTemplateWhats } from "../../../store/contexts/TemplateWhatsContext";
import { useTranslation } from "react-i18next";
import { useBodyField } from "../useBodyField";

const translationPath = "chatBot.page.templates.whatsapp_template.";

const MAX_LENGTH = {
  utility: 550,
  others: 1024,
};

export const BodyField = memo(
  ({
    name,
    label,
    automationId,
    internalTemplateId,
    componentType,
    ...otherProps
  }: TBodyField) => {
    const { t } = useTranslation();

    const { control } = useFormContext();

    const { updateBasicData } = useTemplateWhats();

    const { calcTotalCaracters } = useBodyField();

    const textValue = useWatch({ name: name ?? "body", control });
    const category: IWhatsMetaCategory = useWatch({
      name: "whatsMetaCategory",
      control,
    });
    const characterLimitRequests = useWatch({
      name: "characterLimitRequests",
      control,
    });

    const maxLength =
      category?.name === "UTILITY" ? MAX_LENGTH.utility : MAX_LENGTH.others;

    const totalCharacters = calcTotalCaracters(
      textValue,
      characterLimitRequests,
    );
    const exceeded = totalCharacters > maxLength;

    useEffect(() => {
      updateBasicData(name ?? "body", textValue || "");
    }, [textValue, updateBasicData, name]);

    return (
      <Grid item xs={12} sm={12}>
        <WhatsappSection
          title={t(translationPath + "body")}
          subtitle={t(translationPath + "body_sub")}
        >
          <Controller
            control={control}
            name={name ?? "body"}
            defaultValue={""}
            render={({ fieldState: { error }, field }) => (
              <BodyInput
                field={field}
                error={error}
                fullWidth
                automationId={automationId}
                internalTemplateId={internalTemplateId}
                componentType={componentType}
                multiline
                minRows={4}
                label={label ? label : undefined}
                placeholder={
                  label
                    ? undefined
                    : `${t(translationPath + "body_placeholder")}`
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ marginBottom: 2, alignSelf: "end" }}
                    >
                      <Typography
                        variant="body2"
                        sx={(theme) => ({
                          color: exceeded
                            ? theme.palette.error.main
                            : theme.palette.grey[
                                theme.palette.mode === "light" ? 400 : 600
                              ],
                        })}
                      >
                        {`${totalCharacters}/${maxLength}`}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                {...otherProps}
              />
            )}
          />
        </WhatsappSection>
      </Grid>
    );
  },
);
